import { isClaudePlatformConsoleProvider, PermissionsEnum } from '@novu/shared';
import { RiMore2Fill } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import type { AgentResponse } from '@/api/agents';
import { Badge } from '@/components/primitives/badge';
import { Button } from '@/components/primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/primitives/dropdown-menu';
import { Skeleton } from '@/components/primitives/skeleton';
import { useEnvironment } from '@/context/environment/hooks';
import { useHasPermission } from '@/hooks/use-has-permission';
import { formatDateSimple } from '@/utils/format-date';
import { ClaudeIcon } from '../icons/claude';
import { ExceedsPlanIndicator } from './exceeds-plan-indicator';

type AgentDetailsHeaderProps = {
  agent: AgentResponse | undefined;
  isLoading: boolean;
  onRequestDelete?: (agent: AgentResponse) => void;
};

export function AgentDetailsHeader({ agent, isLoading, onRequestDelete }: AgentDetailsHeaderProps) {
  const has = useHasPermission();
  const { readOnly } = useEnvironment();
  const canWrite = has({ permission: PermissionsEnum.AGENT_WRITE });

  if (isLoading || !agent) {
    return (
      <header className="px-4 pt-2 pb-2 md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-6 w-[20ch]" />
            <Skeleton className="h-4 w-[16ch]" />
          </div>
          <div className="flex shrink-0 gap-3">
            <Skeleton className="size-8 min-w-36 shrink-0 rounded-md" />
            <Skeleton className="size-8 shrink-0 rounded-md" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="px-4 pt-2 pb-2 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-text-strong text-[18px] font-medium leading-6 tracking-tight">{agent.name}</h1>
            {agent.devBridgeActive ? (
              <Badge variant="lighter" color="orange" size="sm">
                LOCAL
              </Badge>
            ) : null}
            {agent.exceedsPlanLimit ? <ExceedsPlanIndicator resource="agent" /> : null}
          </div>
          <div className="flex min-w-0 items-center gap-1">
            <span className="text-text-soft font-mono text-label-xs leading-4 tracking-tight">Created</span>
            <span className="ml-1 text-text-sub font-mono text-label-xs leading-4 tracking-tight">
              {' '}
              {formatDateSimple(agent.createdAt)}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {agent.runtime === 'managed' &&
          agent.managedRuntime?.consoleUrl &&
          isClaudePlatformConsoleProvider(agent.managedRuntime.providerId) ? (
            <Link to={agent.managedRuntime.consoleUrl} target="_blank" rel="noreferrer noopener">
              <Button variant="secondary" mode="outline" size="xs" className="gap-1.5">
                <span>Open in</span>
                <ClaudeIcon className="size-3.5 shrink-0" aria-hidden />
                <span>Claude</span>
              </Button>
            </Link>
          ) : null}
          {canWrite && onRequestDelete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  mode="outline"
                  size="xs"
                  leadingIcon={RiMore2Fill}
                  type="button"
                  className="text-text-sub size-8 min-w-8 shrink-0 gap-0 rounded-md px-0"
                >
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive cursor-pointer"
                  disabled={readOnly}
                  onClick={() => {
                    setTimeout(() => onRequestDelete(agent), 0);
                  }}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
    </header>
  );
}
